// One flexible group deploy: env + which affected repos (any combo) + for
// monorepo repos which apps; fans out one workflow_dispatch per (repo[,app]).
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import { Modal, Spinner, apiErrorMessage } from "./shared";

function dedupeBy(list, key) {
  const seen = new Map();
  (list || []).forEach((x) => {
    const k = x[key];
    if (k != null && !seen.has(k)) seen.set(k, x);
  });
  return [...seen.values()];
}

const prNumber = (url) => {
  const m = /\/pull\/(\d+)/.exec(url || "");
  return m ? `#${m[1]}` : "PR";
};

// Per-(repo, app) deploy via the per-ticket endpoint, normalized to the same
// `{ repoId, app, ok, error }` shape the group-deploy endpoint returns. Used
// for the no-group (adoption / legacy) fan-out path. Module-level so it isn't
// re-created per render and stays within the nesting limit.
const dispatchOne = (ticketKey, envId, repoId, app) =>
  api
    .bssDeployTicket({
      key: ticketKey,
      env: envId,
      repoId,
      ...(app ? { app } : {})
    })
    .then(() => ({ repoId, app: app || null, ok: true }))
    .catch((e) => ({
      repoId,
      app: app || null,
      ok: false,
      error: apiErrorMessage(e, "dispatch failed")
    }));

// `branch` was previously rendered as a top-level row; it's now read per-lane
// from `l.run.branch` because PRs in a group can differ. The prop is no
// longer destructured here — callsites can keep passing it (harmless) but
// the modal ignores it.
// True for lanes whose apps we'll fetch on open (multi-app repo + an actual PR).
const laneWillFetch = (l) =>
  (l.deployWorkflows || []).some((w) => w.app) &&
  !!l.githubRepo &&
  /\/pull\/\d+/.test(l.prUrl || "");

// Fetch a lane's PR "Affected Products" and seed its deploy apps. Module-level
// (keeps the effect's nesting shallow) + fail-soft. Seeds only apps that are
// real deploy targets, and never clobbers an in-progress operator selection.
// Returns the fetch promise so the caller can clear the per-lane loading flag.
const seedLaneApps = (lane, setPickedApps, isCancelled) => {
  const validApps = new Set(
    (lane.deployWorkflows || []).filter((w) => w.app).map((w) => w.app)
  );
  const m = /\/pull\/(\d+)/.exec(lane.prUrl || "");
  if (validApps.size === 0 || !m || !lane.githubRepo) return Promise.resolve();
  return api
    .bssPrAffectedApps(lane.githubRepo, Number(m[1]))
    .then((res) => {
      if (isCancelled()) return;
      const apps = ((res && res.data && res.data.apps) || []).filter((a) =>
        validApps.has(a)
      );
      if (apps.length === 0) return;
      setPickedApps((prev) =>
        (prev[lane.repoId] || []).length > 0
          ? prev
          : { ...prev, [lane.repoId]: apps }
      );
    })
    .catch(() => {});
};

// Clear a lane's apps-loading flag once its fetch settles (no-op if unmounted).
const clearAppsLoading = (setAppsLoading, repoId, isCancelled) => {
  if (isCancelled()) return;
  setAppsLoading((p) => ({ ...p, [repoId]: false }));
};

// Sub-label for a lane's apps section (no nested ternary → lint-clean).
const appsSubLabel = (loading, count) => {
  if (loading) return "reading affected products…";
  if (count === 0) return "pick at least one app";
  return `${count} selected`;
};

export default function GroupDeployModal({
  open,
  onClose,
  groupId,
  ticketKey,
  lanes,
  onDeployed
}) {
  const deployable = useMemo(
    () => (lanes || []).filter((l) => (l.deployWorkflows || []).length > 0),
    [lanes]
  );

  const envs = useMemo(() => {
    const all = deployable.flatMap((l) => l.deployWorkflows || []);
    return dedupeBy(all, "id");
  }, [deployable]);

  const [envId, setEnvId] = useState("");
  const [pickedRepos, setPickedRepos] = useState({});
  const [pickedApps, setPickedApps] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  // Per-repo "fetching affected products" flag → shows a spinner in that lane's
  // apps section while the preselection is being resolved.
  const [appsLoading, setAppsLoading] = useState({});
  // Guards the affected-products fetch to ONCE per modal-open (the ticket page
  // polls, recomputing `deployable` — without this we'd re-fetch every poll).
  const seededAppsRef = useRef(false);

  // Reset ONLY when the modal opens — NOT when `lanes`/`envs` change. The
  // ticket page polls; each poll makes a fresh `lanes` array, recomputing
  // `envs`. Keying this on `envs` wiped the operator's in-progress
  // selection on every refresh.
  useEffect(() => {
    if (!open) return;
    setEnvId("");
    setPickedRepos({});
    setPickedApps({});
    setAppsLoading({});
    setSubmitting(false);
    setError(null);
    setResults(null);
  }, [open]);

  // Default the env once it's available, but only if the operator hasn't
  // chosen one (never clobbers a selection across polls).
  useEffect(() => {
    if (open && !envId && envs.length) setEnvId(envs[0].id);
  }, [open, envId, envs]);

  // Preselect ALL deployable repos by default (the operator can deselect).
  // Seed only while nothing is picked yet — so polling (which makes a fresh
  // `lanes`/`deployable`) never clobbers an in-progress selection, and it still
  // seeds correctly if `lanes` arrive after the modal opens.
  useEffect(() => {
    if (open && deployable.length && Object.keys(pickedRepos).length === 0) {
      setPickedRepos(
        Object.fromEntries(deployable.map((l) => [l.repoId, true]))
      );
    }
  }, [open, deployable, pickedRepos]);

  // Reset the once-per-open fetch guard when the modal closes.
  useEffect(() => {
    if (!open) seededAppsRef.current = false;
  }, [open]);

  // Preselect each multi-app PR's apps from its "Affected Products" checklist
  // (AX→applicant, PX→portal, CRM→crm, SDK→sdk; Docs/DevOps is skipped server-
  // side). Fetched ONCE per open (guarded by the ref) the moment lanes are
  // available. Fail-soft: any error leaves the apps unselected. Never clobbers
  // an in-progress selection (so a poll mid-edit doesn't reset the operator).
  useEffect(() => {
    if (!open || seededAppsRef.current || deployable.length === 0) return;
    seededAppsRef.current = true;
    let cancelled = false;
    const isCancelled = () => cancelled;
    const fetching = deployable.filter(laneWillFetch);
    if (fetching.length === 0) return;
    setAppsLoading(Object.fromEntries(fetching.map((l) => [l.repoId, true])));
    fetching.forEach((l) =>
      seedLaneApps(l, setPickedApps, isCancelled).finally(() =>
        clearAppsLoading(setAppsLoading, l.repoId, isCancelled)
      )
    );
    return () => {
      cancelled = true;
    };
  }, [open, deployable]);

  const isMultiApp = (l) => (l.deployWorkflows || []).some((w) => w.app);
  const appsOf = (l) =>
    dedupeBy(
      (l.deployWorkflows || [])
        .filter((w) => w.app)
        .map((w) => ({ app: w.app, appLabel: w.appLabel || w.app })),
      "app"
    );

  const toggleRepo = (id) => setPickedRepos((p) => ({ ...p, [id]: !p[id] }));
  const toggleApp = (repoId, app) =>
    setPickedApps((p) => {
      const cur = new Set(p[repoId] || []);
      cur.has(app) ? cur.delete(app) : cur.add(app);
      return { ...p, [repoId]: [...cur] };
    });

  const selected = deployable.filter((l) => pickedRepos[l.repoId]);
  const valid =
    !!envId &&
    selected.length > 0 &&
    selected.every(
      (l) => !isMultiApp(l) || (pickedApps[l.repoId] || []).length > 0
    );

  const submit = () => {
    setSubmitting(true);
    setError(null);
    const repos = selected.map((l) =>
      isMultiApp(l)
        ? { repoId: l.repoId, apps: pickedApps[l.repoId] || [] }
        : { repoId: l.repoId }
    );
    // A TicketGroup (fan-out) deploys via the group endpoint, which fans out
    // server-side. An adoption / legacy ticket has no group — fan out on the
    // client via the per-ticket deploy endpoint (one call per repo, and per
    // app for monorepos), then assemble the SAME `{ repoId, app, ok, error }`
    // results shape so the dispatch-results UI is identical either way.
    const dispatch = groupId
      ? api.bssGroupDeploy({ id: groupId, env: envId, repos }).then((res) => {
          const data = (res && res.data) || res || {};
          return data.results || [];
        })
      : Promise.all(
          repos.flatMap((r) => {
            const apps = r.apps && r.apps.length ? r.apps : [undefined];
            return apps.map((app) =>
              dispatchOne(ticketKey, envId, r.repoId, app)
            );
          })
        );
    dispatch
      .then((res) => {
        setResults(res);
        if (res.some((r) => r.ok) && onDeployed) {
          onDeployed(envId);
        }
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not dispatch deploys.")))
      .finally(() => setSubmitting(false));
  };

  if (!open) return null;

  const footer = results ? (
    <button
      type="button"
      className="bss-btn bss-btn--primary"
      onClick={onClose}
    >
      Close
    </button>
  ) : (
    <>
      <button
        type="button"
        className="bss-btn bss-btn--ghost"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="button"
        className="bss-btn bss-btn--primary"
        onClick={submit}
        disabled={!valid || submitting}
      >
        {submitting ? (
          <>
            <Spinner /> Dispatching…
          </>
        ) : (
          <>
            <Icon.PlayCircle size={13} /> Deploy{envId ? ` → ${envId}` : ""}
          </>
        )}
      </button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deploy this ticket"
      width={640}
      footer={footer}
    >
      <div className="bss-ga-modal">
        <div className="bss-ga-meta">
          {ticketKey && (
            <div className="bss-ga-meta-row">
              <div className="bss-ga-meta-k">Ticket</div>
              <div className="bss-ga-meta-v">
                <Icon.Ticket
                  size={12}
                  style={{ color: "var(--bss-boom-pink)" }}
                />
                <span
                  style={{
                    fontFamily: "var(--bss-font-mono)",
                    fontWeight: 500
                  }}
                >
                  {ticketKey}
                </span>
              </div>
            </div>
          )}
          {/* Per-repo PR rows. Each carries its own branch as a secondary
              line — different PRs in a group can be on different branch
              names (parent ticket fan-out, adoption tickets), so a single
              top-level "Branch" row would be misleading. */}
          {deployable.map((l) => {
            const laneBranch = l.run?.branch;
            return (
              <div className="bss-ga-meta-row" key={`pr-${l.repoId}`}>
                <div className="bss-ga-meta-k">{l.repoId}</div>
                <div
                  className="bss-ga-meta-v"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 2
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6
                    }}
                  >
                    <Icon.GitPullRequest size={12} />
                    {l.prUrl ? (
                      <a
                        href={l.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "var(--bss-boom-pink)",
                          textDecoration: "none",
                          fontWeight: 500
                        }}
                      >
                        {(l.githubRepo || l.repoId) + " " + prNumber(l.prUrl)}
                      </a>
                    ) : (
                      <span
                        style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}
                      >
                        {l.githubRepo || l.repoId} — no PR yet
                      </span>
                    )}
                  </div>
                  {laneBranch && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        color: "var(--bss-fg-subtle)",
                        marginLeft: 18
                      }}
                    >
                      <Icon.GitBranch size={10} />
                      <code
                        className="bss-code-inline"
                        style={{ fontSize: 11 }}
                      >
                        {laneBranch}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {deployable.length === 0 ? (
          <div
            style={{
              color: "var(--bss-fg-muted)",
              fontSize: 13,
              padding: "16px 4px"
            }}
          >
            No affected repo has a configured deploy target.
          </div>
        ) : results ? (
          <div className="bss-ga-section">
            <div className="bss-ga-section-hd">
              <span className="bss-ga-section-title">Dispatch results</span>
            </div>
            <div className="bss-ga-meta" style={{ display: "block" }}>
              {results.map((r, i) => (
                <div
                  key={i}
                  className="bss-ga-meta-row"
                  style={{ gridTemplateColumns: "1fr auto" }}
                >
                  <div className="bss-ga-meta-v">
                    <code className="bss-code-inline">{r.repoId}</code>
                    {r.app ? (
                      <span style={{ color: "var(--bss-fg-muted)" }}>
                        · {r.app}
                      </span>
                    ) : null}
                  </div>
                  <span
                    className={`bss-badge ${
                      r.ok ? "bss-badge--green" : "bss-badge--red"
                    }`}
                  >
                    <span className="bss-dot" />
                    {r.ok ? "dispatched" : r.error || "failed"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="bss-ga-section">
              <div className="bss-ga-section-hd">
                <span className="bss-ga-section-title">Environment</span>
                <span className="bss-ga-section-sub">
                  Which staging stack should the selected repos deploy to?
                </span>
              </div>
              <div className="bss-ga-env-grid">
                {envs.map((e) => {
                  const active = envId === e.id;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      className={`bss-ga-env ${active ? "is-active" : ""}`.trim()}
                      onClick={() => setEnvId(e.id)}
                    >
                      <div className="bss-ga-env-radio">
                        <span className="bss-ga-env-radio-dot" />
                      </div>
                      <div className="bss-ga-env-body">
                        <div className="bss-ga-env-name">{e.label || e.id}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bss-ga-section">
              <div className="bss-ga-section-hd">
                <span className="bss-ga-section-title">Repos to deploy</span>
                <span className="bss-ga-section-sub">
                  Pick any combination — api, client, admin.
                </span>
              </div>
              <div className="bss-ga-env-grid">
                {deployable.map((l) => {
                  const active = !!pickedRepos[l.repoId];
                  return (
                    <button
                      key={l.repoId}
                      type="button"
                      className={`bss-ga-env ${active ? "is-active" : ""}`.trim()}
                      onClick={() => toggleRepo(l.repoId)}
                    >
                      <div
                        className="bss-ga-env-radio"
                        style={{ borderRadius: 5 }}
                      >
                        {active && <Icon.Check size={11} />}
                      </div>
                      <div className="bss-ga-env-body">
                        <div className="bss-ga-env-name">{l.repoId}</div>
                        <div className="bss-ga-env-desc">
                          {l.githubRepo || l.repoId}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selected.filter(isMultiApp).map((l) => (
              <div className="bss-ga-section" key={`apps-${l.repoId}`}>
                <div className="bss-ga-section-hd">
                  <span className="bss-ga-section-title">
                    {l.repoId} — apps
                  </span>
                  <span
                    className="bss-ga-section-sub"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6
                    }}
                  >
                    {appsLoading[l.repoId] ? <Spinner /> : null}
                    {appsSubLabel(
                      !!appsLoading[l.repoId],
                      (pickedApps[l.repoId] || []).length
                    )}
                  </span>
                </div>
                <div className="bss-ga-env-grid">
                  {appsOf(l).map((a) => {
                    const on = (pickedApps[l.repoId] || []).includes(a.app);
                    return (
                      <button
                        key={a.app}
                        type="button"
                        className={`bss-ga-env ${on ? "is-active" : ""}`.trim()}
                        onClick={() => toggleApp(l.repoId, a.app)}
                      >
                        <div
                          className="bss-ga-env-radio"
                          style={{ borderRadius: 5 }}
                        >
                          {on && <Icon.Check size={11} />}
                        </div>
                        <div className="bss-ga-env-body">
                          <div className="bss-ga-env-name">{a.appLabel}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {error && (
              <div
                style={{
                  color: "var(--bss-danger)",
                  fontSize: 13,
                  marginTop: 4
                }}
              >
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
